<template>
  <div data-test-attr :class="[$style.item, $style.priceItem]">
    <div :class="$style.label">
      {{ spotlightPriceLabel }}
      <ListingPriceUnit
        :offerType="listing.offerType as OfferType"
        :listingPriceInfo="listingPriceInfo"
      />
    </div>
    <div :class="$style.value">
      <span v-if="grossOrSellingPrice && currency" :class="$style.currency">{{ currency }}</span>
      <ListingPrice
        :currency="currency"
        :offerType="listing.offerType as OfferType"
        :listingPriceInfo="listingPriceInfo"
        :displayCurrency="false"
        :displayPriceUnit="false"
      />
    </div>
    <AdditionalCostTooltip v-if="shouldShowNettoOnlyTooltip" />
    <PriceDropNotification
      v-if="showPriceDropNotification"
      :listing="listing"
      @savePriceDropAlert="$emit('savePriceDropAlert', $event)"
    />
  </div>
</template>

<script lang="ts" setup>
import { Buy, Listing, OfferType, Rent } from '@smg-real-estate/hg-rets-schema';
import type { ListerBranding } from '@smg-real-estate/hg-search-libs';
import { computed } from 'vue';
import { TranslateResult, useI18n } from 'vue-i18n';

import AdditionalCostTooltip from '@/components/AdditionalCostTooltip/AdditionalCostTooltip.vue';
import ListingPrice from '@/components/ListingPrice/ListingPrice.vue';
import ListingPriceUnit from '@/components/ListingPrice/ListingPriceUnit.vue';
import PriceDropNotification from '@/components/PriceDropNotification/PriceDropNotification.vue';
import {
  getListingCurrency,
  getListingDisplayPrice,
  getListingPriceData,
} from '@/helpers/ListingData/ListingData';
import { isPriceDropAlertSupported } from '@/helpers/PriceDropAlert/PriceDropAlertHelper';

const { t } = useI18n();

defineOptions({
  name: 'SpotlightAttributesPrice',
});

interface Props {
  listing: Listing;
  listerBranding: ListerBranding | null;
}
const props = defineProps<Props>();

defineEmits<{
  savePriceDropAlert: [event: unknown];
}>();

const showPriceDropNotification = computed((): boolean => {
  return isPriceDropAlertSupported(
    props.listerBranding?.basePackage,
    props.listerBranding?.subscriptionType,
    props.listing.lister?.id,
    getListingDisplayPrice(props.listing),
  );
});

const spotlightPriceLabel = computed((): TranslateResult => {
  switch (props.listing.offerType) {
    case OfferType.Rent:
      return t('costs.RENT_GROSS');
    case OfferType.Buy:
      return t('PRICE_BUY');
    default:
      throw new Error(`Unsupported offerType ${props.listing.offerType}`);
  }
});

const listingPriceInfo = computed((): Buy | Rent | undefined => {
  return getListingPriceData(props.listing);
});

const grossOrSellingPrice = computed((): number | undefined => {
  return props.listing.prices?.rent?.gross ?? props.listing.prices?.buy?.price;
});

const currency = computed((): string | undefined => {
  return getListingCurrency(props.listing);
});

const shouldShowNettoOnlyTooltip = computed((): boolean => {
  if (props.listing.offerType !== OfferType.Rent) {
    return false;
  }

  const rentData = props.listing.prices?.rent;
  if (!rentData?.net) {
    return false;
  }

  return !rentData?.gross || rentData?.gross === rentData?.net;
});
</script>

<style module lang="scss">
@import './SpotlightAttributes';
</style>
